import type {
  CanvasDocumentV3 as CanvasDocument,
  CanvasEdge,
  CanvasNodeV3 as CanvasNode,
  SeedanceCapabilitiesView,
  SeedanceModel
} from "@seedance/contracts";

type SeedanceNode = Extract<CanvasNode, { kind: "seedance" }>;
type CanvasInputNode = Exclude<CanvasNode, SeedanceNode>;

export interface CanvasPoint {
  x: number;
  y: number;
}

export interface CreateCanvasConnectionInput {
  id: string;
  source: CanvasNode;
  target: CanvasNode;
  existingEdges: readonly CanvasEdge[];
}

export function isCanvasInputNode(node: CanvasNode | undefined): node is CanvasInputNode {
  return node?.kind === "text" ||
    node?.kind === "script" ||
    node?.kind === "image" ||
    node?.kind === "video" ||
    node?.kind === "audio";
}

export function resolveConnectionSources({
  nodes,
  selectedNodeIds,
  startedNodeId
}: {
  nodes: readonly CanvasNode[];
  selectedNodeIds: readonly string[];
  startedNodeId: string;
}): CanvasNode[] {
  const started = nodes.find(({ id }) => id === startedNodeId);
  if (!started) return [];
  if (!isCanvasInputNode(started)) return [];
  const selected = new Set(selectedNodeIds);
  if (!selected.has(started.id)) return [started];
  const inputs = nodes.filter((node): node is CanvasInputNode =>
    selected.has(node.id) && isCanvasInputNode(node)
  );
  return inputs.length > 0 ? inputs : [started];
}

export function createCanvasConnections({
  sources,
  target,
  existingEdges,
  createId
}: {
  sources: readonly CanvasNode[];
  target: CanvasNode;
  existingEdges: readonly CanvasEdge[];
  createId(): string;
}): CanvasEdge[] {
  const created: CanvasEdge[] = [];
  let allEdges = [...existingEdges];
  for (const source of sources) {
    if (source.id === target.id || allEdges.some((edge) =>
      edge.sourceNodeId === source.id && edge.targetNodeId === target.id
    )) continue;
    const edge = createCanvasConnection({
      id: createId(),
      source,
      target,
      existingEdges: allEdges
    });
    if (!edge) continue;
    created.push(edge);
    allEdges = [...allEdges, edge];
  }
  return created;
}

function defaultRole(source: CanvasNode, target: CanvasNode): CanvasEdge["role"] {
  if (target.kind !== "seedance") return "custom";
  if (source.kind === "image" || source.kind === "video" || source.kind === "audio") {
    return "reference";
  }
  return "custom";
}

export function createCanvasConnection({
  id,
  source,
  target,
  existingEdges
}: CreateCanvasConnectionInput): CanvasEdge | undefined {
  if (source.id === target.id) return undefined;
  if (existingEdges.some((edge) => edge.sourceNodeId === source.id && edge.targetNodeId === target.id)) {
    return undefined;
  }
  const edge: CanvasEdge = {
    id,
    sourceNodeId: source.id,
    targetNodeId: target.id,
    role: defaultRole(source, target),
    instruction: "",
    order: existingEdges.reduce((maximum, edge) =>
      edge.targetNodeId === target.id ? Math.max(maximum, edge.order) : maximum, -1
    ) + 1
  };
  return edge;
}

export interface CreateSeedanceVideoGenerationInput {
  source: CanvasNode;
  point: CanvasPoint;
  capabilities: SeedanceCapabilitiesView;
  model?: SeedanceModel;
  createId(): string;
}

export interface SeedanceVideoGeneration {
  node: SeedanceNode;
  edge: CanvasEdge;
}

export interface CreateSeedanceGenerationInput {
  source?: CanvasNode | undefined;
  point: CanvasPoint;
  capabilities: SeedanceCapabilitiesView;
  model: SeedanceModel;
  createId(): string;
}

export interface SeedanceGeneration {
  node: SeedanceNode;
  edge?: CanvasEdge;
}

export interface CreateSeedanceGenerationFromSourcesInput {
  sources: readonly CanvasNode[];
  point: CanvasPoint;
  capabilities: SeedanceCapabilitiesView;
  model: SeedanceModel;
  createId(): string;
}

export interface SeedanceGenerationFromSources {
  node: SeedanceNode;
  edges: CanvasEdge[];
}

export function createSeedanceGeneration({
  source,
  point,
  capabilities,
  model,
  createId
}: CreateSeedanceGenerationInput): SeedanceGeneration | undefined {
  const ratio = capabilities.ratios[0];
  const modelCapability = capabilities.models.find((capability) => capability.id === model);
  const resolution = modelCapability?.resolutions[0];
  if (!modelCapability || !ratio || !resolution) return undefined;
  const node: SeedanceNode = {
    id: createId(),
    kind: "seedance",
    position: point,
    size: { width: 360, height: 240 },
    collapsed: false,
    referenceBindings: [],
    settings: {
      prompt: { segments: [] },
      model,
      ratio,
      durationSeconds: modelCapability.durationSeconds.minimum,
      resolution,
      watermark: false,
      generateAudio: false,
      seed: capabilities.seed.minimum
    }
  };
  if (source === undefined) return { node };
  const edge = createCanvasConnection({
    id: createId(),
    source,
    target: node,
    existingEdges: []
  });
  return edge === undefined ? undefined : { node, edge };
}

export function createSeedanceGenerationFromSources({
  sources,
  point,
  capabilities,
  model,
  createId
}: CreateSeedanceGenerationFromSourcesInput): SeedanceGenerationFromSources | undefined {
  const created = createSeedanceGeneration({
    point,
    capabilities,
    model,
    createId
  });
  if (!created) return undefined;
  return {
    node: created.node,
    edges: createCanvasConnections({
      sources,
      target: created.node,
      existingEdges: [],
      createId
    })
  };
}

export function createSeedanceVideoGeneration({
  source,
  point,
  capabilities,
  model: requestedModel,
  createId
}: CreateSeedanceVideoGenerationInput): SeedanceVideoGeneration | undefined {
  const model = requestedModel ?? capabilities.models[0]?.id;
  if (!model) return undefined;
  const created = createSeedanceGeneration({
    source,
    point,
    capabilities,
    model,
    createId
  });
  if (!created?.edge) return undefined;
  const { node, edge } = created;
  return { node, edge };
}

export function isSeedanceNode(node: CanvasNode | undefined): node is SeedanceNode {
  return node?.kind === "seedance";
}
